import {
  AnnotationChangeType,
  Button,
  Dropdown,
  generateAnnotationId,
  MediaInteractiveCanvas,
} from '@clef/client-library';
import { Annotation, Defect, Label, LabelingType } from '@clef/shared/types';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  SensorDescriptor,
  SensorOptions,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Chip, Grid, MenuItem, MenuList, Tooltip, Typography } from '@material-ui/core';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import ArrowRight from '@material-ui/icons/ArrowRight';
import { TreeItem, TreeView } from '@material-ui/lab';
import cx from 'classnames';
import { truncate } from 'lodash';
import React, { MutableRefObject, useState } from 'react';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import {
  useDefectSelector,
  useDefectSelectorWithArchived,
} from '../../../store/defectState/actions';
import { getDefectColor } from '../../../utils';
import { useLabelingReviewStyles } from '../labelingReviewStyles';
import DefectColor from '@/pages/LabelingTask/components/DefectColor';
import useGetDefectById from '@/hooks/defect/useGetDefectById';
import { ClientFeatures, useFeatureGateEnabled } from '@/hooks/useFeatureGate';
import useGetDefectColorById from '@/hooks/defect/useGetDefectColorById';
import useGetDefectNameById from '@/hooks/defect/useGetDefectNameById';

export interface LabelInstanceProps {
  label: Label;
  isSelected: boolean;
  onSelectLabel?: () => void;
  isVisible: boolean;
  enableSelect: boolean;
  mediaCanvasRef?: MutableRefObject<MediaInteractiveCanvas | null>;
  onToggleVisibility?: (
    visibility: boolean,
    visibleDefect?: number,
    visibleAnnotation?: string,
  ) => void;
  labelingType?: LabelingType;
  showSelectBox?: boolean;
  onSelect?: (id: string) => void;
  userName?: string;
}

const AnnotationInstance: React.FC<{
  onToggleVisibility: LabelInstanceProps['onToggleVisibility'];
  annotation: Annotation;
  defect: Defect;
  onSelect: LabelInstanceProps['onSelect'];
  index: number;
  showDefect?: boolean;
  mediaCanvasRef?: MutableRefObject<MediaInteractiveCanvas | null>;
}> = ({
  onToggleVisibility = () => {},
  annotation,
  defect,
  onSelect,
  index,
  showDefect = false,
  mediaCanvasRef,
}) => {
  const { id } = annotation;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const styles = useLabelingReviewStyles();
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: 'pointer',
    zIndex: isDragging ? 1 : 0,
  };
  const getDefectColorById = useGetDefectColorById();
  const getDefectNameById = useGetDefectNameById();

  const defects = useDefectSelector();
  const [showClassNameTooltip, setShowClassNameTooltip] = useState(false);

  const updateAnnotationClass = (annotationId: string, defectId: number) => {
    const defectColor = getDefectColorById(defectId);
    mediaCanvasRef?.current?.setAnnotations(prev => {
      const newAnnotations = prev.map(ann => {
        if (ann.id !== annotationId) return ann;
        return {
          ...ann,
          defectId,
          data: { ...ann.data, color: defectColor },
        };
      });
      return newAnnotations;
    }, AnnotationChangeType.Edit);
  };

  const defectColorWithTooltip = (
    <Tooltip
      title={getDefectNameById(annotation.defectId)}
      placement="left"
      arrow
      open={showClassNameTooltip}
    >
      <Box
        onMouseEnter={() => setShowClassNameTooltip(true)}
        onMouseLeave={() => setShowClassNameTooltip(false)}
      >
        <DefectColor color={getDefectColorById(annotation.defectId)} />
      </Box>
    </Tooltip>
  );

  const defectColorSelector = (
    <Dropdown
      dropdown={toggleOpen => (
        <MenuList>
          {defects.map(d => (
            <MenuItem
              key={d.id}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                updateAnnotationClass(String(annotation.id), d.id);
                toggleOpen(false);
              }}
            >
              <DefectColor color={getDefectColorById(d.id)} />
              {d.name}
            </MenuItem>
          ))}
        </MenuList>
      )}
      placement="bottom"
    >
      {defectColorWithTooltip}
    </Dropdown>
  );

  return (
    <TreeItem
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      nodeId={'annotation-' + id}
      data-annotation-id={id}
      data-testid="label-preview-annotation"
      onMouseEnter={() => onToggleVisibility(true, defect.id, String(id))}
      onMouseLeave={() => onToggleVisibility(true, defect.id, undefined)}
      onClick={() => {
        if (onSelect) {
          onSelect(String(id));
        }
      }}
      icon={showDefect && (mediaCanvasRef ? defectColorSelector : defectColorWithTooltip)}
      label={
        <Box display="flex" alignItems="center">
          <Typography
            variant="body2"
            className={cx(styles.annotationDetail, styles.labelsTreeItemSpacing)}
          >
            {t('{{index}} - x {{x}}, y {{y}}, {{width}} x {{height}}', {
              index: index + 1,
              x: Math.round(annotation.rangeBox!.xmin),
              y: Math.round(annotation.rangeBox!.ymin),
              width: Math.round(annotation.rangeBox!.xmax - annotation.rangeBox!.xmin),
              height: Math.round(annotation.rangeBox!.ymax - annotation.rangeBox!.ymin),
            })}
          </Typography>
        </Box>
      }
    />
  );
};

const LabelInstanceFlattenAnnotations: React.FC<
  LabelInstanceProps & { sensors: SensorDescriptor<SensorOptions>[] }
> = ({ label, mediaCanvasRef, onSelect, sensors }) => {
  const annotations = (label.annotations as Annotation[]) || [];
  const getDefectById = useGetDefectById();

  if (!label.annotations) {
    return null;
  }

  return (
    <Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={event => {
          const { active, over } = event;
          if (active.id !== over?.id) {
            mediaCanvasRef?.current?.setAnnotations(prev => {
              const oldIndex = prev.findIndex(prev => prev.id === String(active.id));
              const newIndex = prev.findIndex(prev => prev.id === String(over?.id));

              return arrayMove(prev, oldIndex, newIndex).map(ann => ({
                ...ann,
                // We need to modify the id to created some strange "caching" behavior inside canvas
                id: generateAnnotationId(),
              }));
            }, AnnotationChangeType.Edit);
          }
        }}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={annotations.map(ann => ann.id)}
          strategy={verticalListSortingStrategy}
        >
          {annotations.map((annotation, index) => (
            <AnnotationInstance
              key={'id' in annotation ? annotation.id : generateAnnotationId()}
              annotation={annotation}
              defect={getDefectById(annotation.defectId)}
              onSelect={onSelect}
              index={index}
              showDefect
              mediaCanvasRef={mediaCanvasRef}
              onToggleVisibility={undefined}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
};

const LabelInstance: React.FC<LabelInstanceProps> = ({
  label,
  mediaCanvasRef,
  enableSelect,
  isSelected,
  onSelectLabel = () => {},
  isVisible,
  onToggleVisibility = () => {},
  labelingType,
  showSelectBox = true,
  onSelect,
  userName,
}) => {
  const allUsers = useTypedSelector(state => state.user.users);
  const styles = useLabelingReviewStyles();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Ensure items can still respond to click event. See https://github.com/clauderic/dnd-kit/issues/591#issuecomment-1017050816
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const annotations = (label.annotations as Annotation[]) || [];
  const allDefects = useDefectSelectorWithArchived();
  const labelerName = allUsers?.find(_ => _.id === label.labelerName)?.name || '';
  const labelerDisplayName = truncate(labelerName, { length: 12 });

  const showBoxOrdering = useFeatureGateEnabled(ClientFeatures.ObjectDetectionSupportOrdering);
  if (!allDefects) {
    return null;
  }
  return (
    <div
      className={cx(styles.labelInstance, !isVisible && 'labelHidden')}
      data-testid="label-instance"
      aria-hidden={!isVisible}
      onMouseEnter={() => onToggleVisibility(true)}
      onMouseLeave={() => onToggleVisibility(false)}
    >
      <TreeView
        defaultCollapseIcon={<ArrowDropDown />}
        defaultExpandIcon={<ArrowRight />}
        defaultExpanded={[labelerName, ...allDefects.map(defect => 'defect-' + defect.id)]}
        disableSelection
      >
        {/* tree level 1 - labeler */}
        <TreeItem
          nodeId={labelerName}
          label={
            <Grid
              container
              alignItems="center"
              wrap="nowrap"
              className={styles.labelsTreeItemSpacing}
            >
              <Typography variant="body2" title={labelerName}>
                <strong>{`${userName || labelerDisplayName} (${annotations.length})`}</strong>
              </Typography>
              <div className={styles.flexGrow} />
              {isSelected && showSelectBox && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="primary"
                  component="span"
                  label={t('Selected')}
                  clickable={false}
                />
              )}
              {!isSelected && isVisible && enableSelect && showSelectBox && (
                <Button
                  id="select-label-button"
                  color="primary"
                  size="small"
                  className={styles.labelSelectBtn}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelectLabel();
                  }}
                >
                  {t('Select')}
                </Button>
              )}
            </Grid>
          }
        >
          {showBoxOrdering && (
            <LabelInstanceFlattenAnnotations
              label={label}
              mediaCanvasRef={mediaCanvasRef}
              enableSelect={enableSelect}
              isSelected={isSelected}
              onSelectLabel={onSelectLabel}
              isVisible={isVisible}
              onToggleVisibility={onToggleVisibility}
              labelingType={labelingType}
              showSelectBox={showSelectBox}
              onSelect={onSelect}
              userName={userName}
              sensors={sensors}
            />
          )}
          {!showBoxOrdering &&
            allDefects?.map(defect => {
              const annotationsForThisDefect = annotations.filter(
                ann => ann.defectId === defect.id,
              );
              if (annotationsForThisDefect.length === 0) {
                return null;
              }
              return (
                // tree level 2 - defect
                <TreeItem
                  key={defect.id}
                  nodeId={'defect-' + defect.id}
                  data-defect-id={defect.id}
                  data-testid="label-preview-defect"
                  onMouseEnter={() => onToggleVisibility(true, defect.id)}
                  onMouseLeave={() => onToggleVisibility(true, undefined)}
                  label={
                    <Grid container alignItems="center" className={styles.labelsTreeItemSpacing}>
                      <div
                        className={styles.defectIndicator}
                        style={{ backgroundColor: getDefectColor(defect) }}
                      />
                      <Typography variant="body2">
                        {labelingType === LabelingType.DefectBoundingBox
                          ? `${defect.name} (${annotationsForThisDefect.length})`
                          : defect.name}
                      </Typography>
                    </Grid>
                  }
                >
                  {labelingType === LabelingType.DefectBoundingBox && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={event => {
                        const { active, over } = event;
                        if (active.id !== over?.id) {
                          mediaCanvasRef?.current?.setAnnotations(prev => {
                            const oldIndex = prev.findIndex(prev => prev.id === String(active.id));
                            const newIndex = prev.findIndex(prev => prev.id === String(over?.id));

                            return arrayMove(prev, oldIndex, newIndex).map(ann => ({
                              ...ann,
                              // We need to modify the id to created some strange "caching" behavior inside canvas
                              id: generateAnnotationId(),
                            }));
                          }, AnnotationChangeType.Edit);
                        }
                      }}
                      modifiers={[restrictToVerticalAxis]}
                    >
                      <SortableContext
                        items={annotationsForThisDefect.map(ann => ann.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {annotationsForThisDefect.map((annotation, index) => {
                          return (
                            // tree level 3 - annotation
                            <AnnotationInstance
                              index={index}
                              defect={defect}
                              annotation={annotation}
                              key={annotation.id}
                              onSelect={onSelect}
                              onToggleVisibility={onToggleVisibility}
                            />
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  )}
                </TreeItem>
              );
            })}
        </TreeItem>
      </TreeView>
    </div>
  );
};

export default LabelInstance;
